import { useId, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { t } from "../../i18n/t";
import { kommuneSummaries, searchKommuner } from "../../lib/national";
import { routes } from "../../lib/routes";
import { useNational } from "../../lib/useNational";
import { LoadError } from "./LoadError";

const MAX_RESULTS = 8;

/** Search box over the kommuner in the national file; Enter on a single match opens it. */
export function KommuneSearch({ showError = true }: { showError?: boolean }) {
  const id = useId();
  const navigate = useNavigate();
  const national = useNational();
  const [query, setQuery] = useState("");
  const summaries = useMemo(
    () => (national.data ? kommuneSummaries(national.data) : []),
    [national.data],
  );
  const matches = useMemo(() => searchKommuner(summaries, query, MAX_RESULTS), [summaries, query]);
  const typed = query.trim() !== "";
  const only = matches.length === 1 ? matches[0] : undefined;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (only) navigate(routes.kommunePublic(only.nr));
  }

  return (
    <form className="kommune-search" role="search" onSubmit={onSubmit}>
      <label htmlFor={id}>{t("kommuneSearch.label")}</label>
      <input
        id={id}
        type="search"
        autoComplete="off"
        value={query}
        placeholder={t("kommuneSearch.placeholder")}
        onChange={(e) => setQuery(e.target.value)}
      />
      {showError && national.error && (
        <LoadError message={t("kommuneSearch.loadFailed")} onRetry={national.reload} />
      )}
      {typed && matches.length > 0 && (
        <ul className="plain-list kommune-results" aria-label={t("kommuneSearch.results")}>
          {matches.map((k) => (
            <li key={k.nr}>
              <Link to={routes.kommunePublic(k.nr)}>
                <span className="picker-name">{k.name}</span>
                <span className="muted">{k.nr}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {typed && national.data && matches.length === 0 && (
        <p className="muted" role="status">
          {t("kommuneSearch.noMatch", { query: query.trim() })}
        </p>
      )}
    </form>
  );
}
